import { CheckCircle2, MapPin, ShieldCheck, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

const PILLARS = [
  { icon: ShieldCheck, title: "Licensed & Insured", body: "Fully insured, WCB covered, and built around safe job-site practices." },
  { icon: Wrench, title: "Self-Performing Crew", body: "From the dig to the pour, we keep accountability under one roof — no finger-pointing between subs." },
  { icon: MapPin, title: "Local to BC", body: "Based on Vancouver Island, working across the Island and the Lower Mainland." },
];

export default function About() {
  usePageTitle("About Us", "Meet the crew behind AV Contracting — 15+ years of excavation, concrete and site work across Vancouver Island and the Lower Mainland. Licensed, insured, WCB.");
  return (
    <div className="pt-20 md:pt-24" data-testid="about-page">
      {/* HEADER */}
      <section className="bg-[#2C3539] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-[#9CC4DB] text-xs tracking-[0.35em] font-bold uppercase mb-3">About AV Contracting</div>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.92]">The crew<br/>behind <span className="text-[#9CC4DB]">the work.</span></h1>
          </div>
          <div className="lg:col-span-5 self-end">
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              Amazing View Contracting started with a small crew, a Kubota and a refusal to leave any detail to chance.
              Today we run full-scope excavation, concrete and site-prep packages across British Columbia — from residential
              foundations through heavy-civil and commercial work.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#23282B]/10">
          {PILLARS.map((p) => (
            <div key={p.title} className="bg-white p-8 md:p-10" data-testid={`about-pillar-${p.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <p.icon size={32} className="text-[#2F5F80]" />
              <div className="font-display text-2xl md:text-3xl uppercase mt-4 text-[#23282B]">{p.title}</div>
              <p className="mt-2 text-[#23282B]/70 text-sm md:text-base">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="bg-[#F1F3F5]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <img
            src="https://customer-assets.emergentagent.com/job_av-job-command/artifacts/8bkv3r3m_484378192_1085944793336493_7734496098863663277_n.jpg"
            alt="Excavator on site"
            className="w-full h-[420px] object-cover"
          />
          <div>
            <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase mb-3">Our Story</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-[1] text-[#23282B]">A crew that<br/>takes ownership.</h2>
            <div className="mt-5 space-y-3 text-[#23282B]/80 text-base leading-relaxed">
              <p>We've been pouring foundations and breaking ground in BC for over fifteen years. Our work shows up on driveways, in basements, around backyard patios, and underneath the homes families build their lives on.</p>
              <p>Because we self-perform — the dig, the forms, the pour, the drainage and the final grade — there's one crew accountable for the whole job. That means tighter schedules, cleaner handoffs, and a finished product we're willing to put our name on.</p>
              <p>We're proud of our finish work — and even prouder of how we leave a job site.</p>
            </div>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Residential to heavy civil & commercial",
                "Free written estimates",
                "Modern equipment, GPS-guided",
                "On-time, on-spec, on-budget",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2 text-[#23282B]"><CheckCircle2 size={18} className="text-[#2F5F80] mt-0.5" /> {b}</li>
              ))}
            </ul>
            <Link to="/contact" data-testid="about-cta" className="mt-8 inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-7 py-3.5 uppercase transition-colors">
              Start Your Project
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-[#23282B] text-white" data-testid="about-service-area">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[#9CC4DB] text-xs tracking-[0.35em] font-bold uppercase mb-3">Where We Work</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-[1]">Island &amp; Mainland.</h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-white/80 text-base leading-relaxed">
            <p>
              Home base is Langford on Vancouver Island, and most of our work runs through the Greater Victoria area —
              Langford, Colwood, Sooke, Metchosin, Saanich and Victoria — up-Island to Duncan, Cowichan, Nanaimo and beyond.
              For larger packages we also mobilize to the Lower Mainland.
            </p>
            <p>
              Coastal BC ground is its own animal: winter atmospheric rivers, clay pockets, bedrock where you least want it,
              and drainage rules that change by municipality. Fifteen years of digging in it means we quote accurately,
              plan for what's actually under the surface, and build for the climate the work has to survive.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
}
